import { Button, Carousel, Stack } from '@customink/pigment-react';
import { styled } from '@customink/pigment-react/lib/themeCustomink';
import { Grid } from '@mui/material';
import { MouseEventHandler } from 'react';
import { useAuth } from '~/contexts/Auth/AuthContext';
import { OrderSummary } from '~/adapters/services/accounts/orders/types';
import { CarouselProductImage } from '~/components/Image/CarouselProductImage';
import { BoxVisibleAboveMd, BoxVisibleBelowMd } from '~/components/Visibility/VisibleBox';
import { useOrderButtonConfigs } from '~/pages/OrderHistory/OrdersList/OrderCard/OrderContentRow/useOrderButtonConfigs';
import { OrderDetailToggle } from '../../../components/OrderDetailToggle';

const fallbackActionsWidth = '300px';
const fallbackCarouselWidth = 200;

interface OrderContentRowProps {
    order: OrderSummary;
    detailOpen: boolean;
    toggle: MouseEventHandler<HTMLElement>;
    singleDetail: boolean;
}

const OrderContent = styled(Stack)(({ theme }) => ({
    [theme.breakpoints.up('md')]: {
        flexDirection: 'row',
        padding: theme.spacing(2),
    },
}));

const OrderActions = styled(Stack)(({ theme }) => ({
    width: fallbackActionsWidth,
    [theme.breakpoints.down('md')]: {
        width: '100%',
        padding: theme.spacing(2),
    },
}));

const OrderContentGrid = styled(Grid)(({ theme }) => ({
    [theme.breakpoints.down('md')]: {
        padding: theme.spacing(1.5),
    },
}));

export function OrderContentRow({ order, detailOpen, singleDetail, toggle }: OrderContentRowProps) {
    const { isImpersonatedExperience } = useAuth();
    const slides = order.proofs.map((uri) => <CarouselProductImage alt="Product preview" src={uri} key={uri} />);

    const buttonConfigs = useOrderButtonConfigs(order, isImpersonatedExperience);

    return (
        <OrderContent>
            <OrderContentGrid container>
                <Grid item sx={{ width: '50%', maxWidth: fallbackCarouselWidth }}>
                    <Carousel slides={slides} sliderHeight="auto" loop={false} />
                </Grid>
                <Grid
                    item
                    sx={{
                        px: 2,
                    }}
                >
                    <Stack justifyContent="space-between" sx={{ height: '100%' }}>
                        <Stack direction="column" gap={1}>
                            <div>Design Name</div>
                            <b>{order.designName}</b>
                            <div>Quantity</div>
                            <b>{order.quantity}</b>
                        </Stack>
                        <BoxVisibleAboveMd>
                            <OrderDetailToggle singleDetail={singleDetail} toggle={toggle} open={detailOpen} />
                        </BoxVisibleAboveMd>
                    </Stack>
                </Grid>
            </OrderContentGrid>
            <OrderActions spacing={2}>
                {buttonConfigs.map((config) => (
                    <Button key={config.type} variant={config.variant} href={config.href} onClick={config.onClick}>
                        {config.label}
                    </Button>
                ))}
                <BoxVisibleBelowMd>
                    <OrderDetailToggle toggle={toggle} open={detailOpen} singleDetail={singleDetail} />
                </BoxVisibleBelowMd>
            </OrderActions>
        </OrderContent>
    );
}
